import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { differenceInDays, format, parseISO } from 'date-fns'

export default function Admin({ session }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [users, setUsers] = useState([])

  const loadAllUsers = useCallback(async () => {
    const [profilesRes, seasonsRes, matchesRes] = await Promise.all([
      supabase.from('profiles').select('*').order('full_name'),
      supabase.from('seasons').select('*').eq('is_current', true),
      supabase.from('matches').select('user_id, goals, assists'),
    ])

    const profiles = profilesRes.data || []
    const seasons = seasonsRes.data || []
    const matches = matchesRes.data || []

    const enriched = profiles.map(p => {
      const season = seasons.find(s => s.user_id === p.id) || null
      const userMatches = matches.filter(m => m.user_id === p.id)
      return {
        ...p,
        season,
        apps: userMatches.length,
        goals: userMatches.reduce((a, m) => a + (m.goals || 0), 0),
        assists: userMatches.reduce((a, m) => a + (m.assists || 0), 0),
      }
    })

    setUsers(enriched)
    setLoading(false)
  }, [])

  useEffect(() => {
    supabase.from('profiles').select('is_admin').eq('id', session.user.id).single()
      .then(({ data }) => {
        if (!data?.is_admin) {
          navigate('/')
        } else {
          setIsAdmin(true)
          loadAllUsers()
        }
      })
  }, [session, navigate, loadAllUsers])

  if (loading) return <div style={{ color: 'var(--muted)', padding: 40 }}>Loading...</div>
  if (!isAdmin) return null

  const now = new Date()

  const daysUntilExpiry = (contractEnd) => {
    if (!contractEnd) return null
    return differenceInDays(parseISO(contractEnd), now)
  }

  const contractUrgency = (contractEnd) => {
    const days = daysUntilExpiry(contractEnd)
    if (days === null) return null
    if (days < 0) return 'expired'
    if (days <= 30) return 'danger'
    if (days <= 90) return 'warning'
    return null
  }

  const expiringThisMonth = users.filter(u => {
    const days = daysUntilExpiry(u.season?.contract_end)
    return days !== null && days >= 0 && days <= 30
  }).length

  const expiringIn90 = users.filter(u => {
    const days = daysUntilExpiry(u.season?.contract_end)
    return days !== null && days >= 0 && days <= 90
  }).length

  return (
    <div>
      {/* Header */}
      <div className="mb-32">
        <div className="page-title">Admin</div>
        <div className="page-sub">Member overview & contract tracker</div>
      </div>

      {/* Summary cards */}
      <div className="grid-3 mb-32">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Active Members</div>
        </div>
        <div className="stat-card" style={expiringIn90 > 0 ? { borderColor: 'var(--gold-border)' } : {}}>
          <div className="stat-value" style={{ color: expiringIn90 > 0 ? 'var(--gold)' : 'var(--text)' }}>{expiringIn90}</div>
          <div className="stat-label">Contracts in 90 Days</div>
        </div>
        <div className="stat-card" style={expiringThisMonth > 0 ? { borderColor: 'rgba(229,57,53,0.35)' } : {}}>
          <div className="stat-value" style={{ color: expiringThisMonth > 0 ? 'var(--danger)' : 'var(--text)' }}>{expiringThisMonth}</div>
          <div className="stat-label">Expiring This Month</div>
        </div>
      </div>

      {/* Users table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th>Player</th>
                <th>Email</th>
                <th>Club</th>
                <th>Country</th>
                <th>Contract Expiry</th>
                <th>Last Seen</th>
                <th style={{ textAlign: 'center' }}>Apps</th>
                <th style={{ textAlign: 'center' }}>Goals</th>
                <th style={{ textAlign: 'center' }}>Assists</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const urgency = contractUrgency(u.season?.contract_end)
                const days = daysUntilExpiry(u.season?.contract_end)

                const rowBg =
                  urgency === 'danger' ? 'rgba(229,57,53,0.06)' :
                  urgency === 'warning' ? 'rgba(253,215,0,0.04)' :
                  undefined

                const contractColor =
                  urgency === 'danger' || urgency === 'expired' ? 'var(--danger)' :
                  urgency === 'warning' ? 'var(--gold)' :
                  'var(--text)'

                return (
                  <tr key={u.id} style={rowBg ? { background: rowBg } : {}}>
                    {/* Player */}
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>
                        {u.full_name || <span style={{ color: 'var(--muted)' }}>—</span>}
                      </div>
                      {u.position && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{u.position}</div>
                      )}
                    </td>

                    {/* Email */}
                    <td style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {u.email || '—'}
                    </td>

                    {/* Club */}
                    <td style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                      {u.season?.club_name || <span style={{ color: 'var(--muted)', fontSize: 13 }}>No season</span>}
                    </td>

                    {/* Country */}
                    <td style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {u.season?.country || '—'}
                    </td>

                    {/* Contract expiry */}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {u.season?.contract_end ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600, color: contractColor }}>
                            {format(parseISO(u.season.contract_end), 'dd MMM yyyy')}
                          </div>
                          <div style={{ fontSize: 11, color: contractColor, opacity: 0.75, marginTop: 1 }}>
                            {urgency === 'expired'
                              ? 'Expired'
                              : `${days}d remaining`}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--muted)', fontSize: 13 }}>—</span>
                      )}
                    </td>

                    {/* Last seen */}
                    <td style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {u.last_seen_at
                        ? format(parseISO(u.last_seen_at), 'dd MMM yyyy')
                        : '—'}
                    </td>

                    {/* Stats */}
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: 20, color: 'var(--gold)', letterSpacing: 1 }}>{u.apps}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: 20, color: 'var(--gold)', letterSpacing: 1 }}>{u.goals}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: 20, color: 'var(--gold)', letterSpacing: 1 }}>{u.assists}</span>
                    </td>
                  </tr>
                )
              })}

              {users.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px 0' }}>
                    No members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
